// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/openItemChip.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { askPunttOpenItemKindSchema } from "@mg/schemas/src/christo/askPunttOpenItems";

import { type AskV2OpenItemContext } from "./types";

// why: the composer sends a picked open item as this block before the person's
// words (`composeMessageWithOpenItem`), so the model and close_open_item get the
// item id. Shown raw, the bubble read "[Open item source_conflict 000df92...]
// Coordinator User (Coordinator) disagrees ..." (L2.19); the chip shows it instead.
// why the first blank line ends the block: `composeMessageWithOpenItem` joins the need onto one line, so only the
// person's words follow one; warhol's `split_open_item` reads the same pattern (warhol test_open_item_block_parity.py).
const OPEN_ITEM_BLOCK =
  /^\[Open item ([a-z_]+) ([^\]\s]+)\] ([\s\S]*?)(?:\n\n([\s\S]*))?$/;

/** The open item a sent message carries as its leading block, and the person's own words after it. */
export function splitOpenItemBlock(
  content: string,
): { context: AskV2OpenItemContext; text: string } | null {
  const match = OPEN_ITEM_BLOCK.exec(content);
  if (!match) return null;
  // why the cast: groups 1-3 are not optional in OPEN_ITEM_BLOCK, so a match always holds them; only 4 may be absent.
  const [kindText, id, neededText, text] = match.slice(1) as [
    string,
    string,
    string,
    string | undefined,
  ];
  const kind = askPunttOpenItemKindSchema.safeParse(kindText);
  const needed = neededText.trim();
  if (!kind.success || needed.length === 0) return null;
  return { context: { id, kind: kind.data, needed }, text: text ?? "" };
}

/**
 * A sent message as the thread shows it: the open item block becomes its chip
 * and the bubble keeps the person's own words. Display only -- the stored
 * message, and what the model receives, keep the block.
 */
export function withOpenItemChip(message: AskPunttMessage): AskPunttMessage {
  if (message.role !== "user" || typeof message.content !== "string") {
    return message;
  }
  const split = splitOpenItemBlock(message.content);
  if (!split) return message;
  const hasOpenItem = (message.openItemContexts?.length ?? 0) > 0;
  return {
    ...message,
    content: split.text,
    openItemContexts: hasOpenItem ? message.openItemContexts : [split.context],
  };
}
